import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface QueueHealth {
  status: 'healthy' | 'degraded' | 'critical';
  pendingCount: number;
  processingCount: number;
  oldestPendingAge: number; // milliseconds
  avgProcessingTime: number; // milliseconds
  recentFailureRate: number; // percentage
  avgPriority: number; // average priority of pending jobs
}

export function useQueueHealthMonitor() {
  const [health, setHealth] = useState<QueueHealth>({
    status: 'healthy',
    pendingCount: 0,
    processingCount: 0,
    oldestPendingAge: 0,
    avgProcessingTime: 0,
    recentFailureRate: 0,
    avgPriority: 5,
  });

  const [lastAlertTime, setLastAlertTime] = useState<number>(0);
  const ALERT_COOLDOWN = 5 * 60 * 1000; // 5 minutes

  const checkHealth = async () => {
    try {
      const { data: jobs } = await supabase
        .from('analysis_job_queue')
        .select('*');

      if (!jobs) return;

      const pending = jobs.filter(j => j.status === 'pending');
      const processing = jobs.filter(j => j.status === 'processing');
      
      // Calculate oldest pending job age
      const oldestPending = pending
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];
      const oldestAge = oldestPending 
        ? Date.now() - new Date(oldestPending.created_at).getTime()
        : 0;

      // Calculate recent failure rate (last hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const recentJobs = jobs.filter(j => j.created_at >= oneHourAgo);
      const recentFailed = recentJobs.filter(j => j.status === 'failed').length;
      const failureRate = recentJobs.length > 0 
        ? (recentFailed / recentJobs.length) * 100 
        : 0;

      // Calculate avg processing time for completed jobs
      const completed = jobs.filter(j => j.status === 'completed' && j.processing_time_ms);
      const avgTime = completed.length > 0
        ? completed.reduce((sum, j) => sum + (j.processing_time_ms || 0), 0) / completed.length
        : 0;

      // Calculate average priority of pending jobs (lower = more urgent)
      const avgPriority = pending.length > 0
        ? pending.reduce((sum, j) => sum + (j.priority || 5), 0) / pending.length
        : 5;

      // Determine health status
      let status: QueueHealth['status'] = 'healthy';
      
      if (
        pending.length > 50 ||
        oldestAge > 10 * 60 * 1000 || // 10 minutes
        failureRate > 30
      ) {
        status = 'critical';
      } else if (
        pending.length > 20 ||
        oldestAge > 5 * 60 * 1000 || // 5 minutes
        failureRate > 15
      ) {
        status = 'degraded';
      }

      const newHealth: QueueHealth = {
        status,
        pendingCount: pending.length,
        processingCount: processing.length,
        oldestPendingAge: oldestAge,
        avgProcessingTime: avgTime,
        recentFailureRate: failureRate,
        avgPriority: avgPriority,
      };

      setHealth(newHealth);

      // Alert on critical status (with cooldown)
      const now = Date.now();
      if (status === 'critical' && now - lastAlertTime > ALERT_COOLDOWN) {
        toast.error('Queue Health Critical', {
          description: `${pending.length} pending jobs, ${Math.round(oldestAge / 1000)}s oldest, ${failureRate.toFixed(1)}% failure rate`,
        });
        setLastAlertTime(now);
      }
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30s

    const channel = supabase
      .channel('queue-health')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analysis_job_queue',
        },
        () => {
          checkHealth();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      channel.unsubscribe();
    };
  }, [lastAlertTime]);

  return health;
}
